<template>
  <span
    ref="tooltip"
    v-clickoutside="hideTooltip"
    class="base-tooltip"
    @mouseenter="mouseEnterHandler"
    @mouseleave="mouseLeaveHandler"
    @click="clickHandler">
    <span><slot v-bind="{ isShowing }" /></span>
  </span>
</template>

<script>
import clickoutside from 'Root/directives/clickoutside';
import { mapState } from 'vuex';
import { uuidv4 } from 'Root/helpers/tools';

export default {
  name: 'BaseTooltip',

  directives: {
    ...clickoutside,
  },

  props: {
    // CSS валидный токен
    // будет использоваться для выдачи уникального CSS класса
    token: {
      type: String,
      default: () => uuidv4(),
    },

    // поддерживается только top и bottom, остальное реализовываем по необходимости
    position: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom'].includes(value),
    },

    triggers: {
      type: Array,
      default: () => (['hover', 'manual', 'click']),
    },

    // для manual триггера
    shown: {
      type: Boolean,
      default: false,
    },

    type: {
      type: String,
      default: null,
      validator: (val) => [
        'info',
        'info-gray',
        'error',
        'executor-card',
        'warning-red',
        'warning-orange',
        'warning-yellow',
        'executor-card',
        'entry-user',
        'header-profile',
        null,
      ].includes(val),
    },

    zIndex: {
      type: [String, Number],
      default: 100,
    },
  },

  data() {
    return {
      isCreated: false, // создаем только 1 раз, дальше показываем\скрываем
      isShowing: false,
    };
  },

  computed: {
    ...mapState(['userDevice']),
  },

  watch: {
    shown(newValue, oldValue) {
      if (this.triggers.includes('manual') && newValue !== oldValue) {
        if (newValue) {
          this.showTooltip();
        } else {
          this.hideTooltip();
        }
      }
    },
  },

  mounted() {
    if (this.triggers?.includes('manual') && this.shown) {
      this.showTooltip();
    }
  },

  beforeDestroy() {
    this.deleteTooltip();
  },

  methods: {
    clickHandler() {
      if (!this.triggers.includes('click')) {
        return;
      }

      if (this.isShowing) {
        this.hideTooltip();
      } else {
        this.showTooltip();
      }
    },

    mouseEnterHandler() {
      if (!this.triggers.includes('hover') || (this.triggers.includes('click') && this.userDevice.isCoarse)) {
        return;
      }

      this.showTooltip();
    },

    mouseLeaveHandler(e) {
      if (!this.triggers.includes('hover') || (this.triggers.includes('click') && this.userDevice.isCoarse)) {
        return;
      }

      this.hideTooltip(e);
    },

    showTooltip() {
      if (this.isCreated) {
        // если создан - показываем
        this.$store.commit('tooltip/mutateSetTooltipVisibility', {
          token: this.token,
          status: true,
        });
        this.isShowing = true;
      } else {
        // если не создан - создаем, по умолчанию создается сразу с показом
        this.createTooltip();
      }
    },

    hideTooltip(event) {
      if (this.isCreated) {
        if (this.checkTooltipIsTarget(event)) {
          return;
        }

        this.$emit('hideTooltip');
        this.$store.commit('tooltip/mutateSetTooltipVisibility', {
          token: this.token,
          status: false,
        });
        this.isShowing = false;
      }
    },

    deleteTooltip() {
      this.$store.commit('tooltip/mutateDeleteTooltip', this.token);
    },

    createTooltip() {
      requestAnimationFrame(() => {
        this.$store.commit('tooltip/mutateAddTooltip', {
          token: this.token,
          tooltip: {
            isVisible: true,
            token: this.token, // чтобы v-for в TooltipLayer не плакался на key === index
            content: this.$slots.content,
            container: this.$refs.tooltip,
            position: this.position,
            type: this.type,
            zIndex: this.zIndex,
            triggers: this.triggers,
          },
        });

        this.isCreated = true;
        this.isShowing = true;
      });
    },

    checkTooltipIsTarget(event) {
      if (!event) {
        return false;
      }

      const { target, toElement } = event;
      return !!(toElement || target).closest(`.tooltip__balloon--${this.token}`);
    },
  },
};
</script>

<style scoped>
  .base-tooltip {
    display: inline-block;
  }
</style>
